import React from 'react';
import { X, Calculator, TrendingUp, DollarSign, User, Target, Clock, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

const B2CROIForm = ({ data, errors, onChange, onCalculate, onBack, onClose }) => {
  const { t } = useTranslation();

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-testid="roi-calculator-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <User className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('roiCalculator.individual.formTitle')}</h2>
                <p className="text-emerald-100 text-sm">{t('roiCalculator.individual.formSubtitle')}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-5">
            <button onClick={onBack} className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
              ← {t('roiCalculator.selectType').split(':')[0]}
            </button>
            
            {/* Current Salary */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="w-4 h-4 text-emerald-600" />
                {t('roiCalculator.individual.currentSalary')} *
              </label>
              <input
                type="text"
                value={formatNumber(data.currentSalary)}
                onChange={(e) => onChange('currentSalary', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.currentSalary ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="750,000"
              />
              {errors.currentSalary && <p className="text-red-500 text-xs mt-1">{errors.currentSalary}</p>}
            </div>

            {/* Target Salary Increase */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                {t('roiCalculator.individual.targetSalaryIncrease')} *
              </label>
              <input
                type="text"
                value={data.targetSalaryIncrease}
                onChange={(e) => onChange('targetSalaryIncrease', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.targetSalaryIncrease ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="15"
              />
              {errors.targetSalaryIncrease && <p className="text-red-500 text-xs mt-1">{errors.targetSalaryIncrease}</p>}
            </div>

            {/* Career Goal */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Target className="w-4 h-4 text-emerald-600" />
                {t('roiCalculator.individual.careerGoal')} *
              </label>
              <select
                value={data.careerGoal}
                onChange={(e) => onChange('careerGoal', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="promotion">{t('roiCalculator.individual.goals.promotion')}</option>
                <option value="transition">{t('roiCalculator.individual.goals.transition')}</option>
                <option value="leadership">{t('roiCalculator.individual.goals.leadership')}</option>
                <option value="balance">{t('roiCalculator.individual.goals.balance')}</option>
              </select>
            </div>

            {/* Sessions per Month */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 text-emerald-600" />
                {t('roiCalculator.individual.sessionsPerMonth')} *
              </label>
              <input
                type="text"
                value={data.sessionsPerMonth}
                onChange={(e) => onChange('sessionsPerMonth', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.sessionsPerMonth ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="4"
              />
              {errors.sessionsPerMonth && <p className="text-red-500 text-xs mt-1">{errors.sessionsPerMonth}</p>}
            </div>

            {/* Months of Coaching */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 text-emerald-600" />
                {t('roiCalculator.individual.monthsOfCoaching')} *
              </label>
              <input
                type="text"
                value={data.monthsOfCoaching}
                onChange={(e) => onChange('monthsOfCoaching', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.monthsOfCoaching ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="6"
              />
              {errors.monthsOfCoaching && <p className="text-red-500 text-xs mt-1">{errors.monthsOfCoaching}</p>}
            </div>

            <Button
              onClick={onCalculate}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 text-lg font-semibold rounded-lg flex items-center justify-center gap-2"
            >
              <Calculator className="w-5 h-5" />
              {t('roiCalculator.calculateBtn')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2CROIForm;
